// "Location is off" popup for field users on a phone.
//
// Field work (punch-in, Start Call, route km) needs the phone's location. When
// it's blocked for the app, not yet allowed, or the phone's location (GPS) is
// switched off, this covers the screen until it's back on:
//   * not yet allowed  -> "Allow location" asks the browser's own permission box
//   * phone GPS off    -> "Turn on location" asks for a fix with high accuracy;
//                         on Android Chrome that brings up the phone's own
//                         "Turn on device location" box (a web app can't open
//                         the Settings screen itself), plus steps as a fallback
//   * blocked for app  -> step-by-step path to the setting (iPhone / Android,
//                         installed app / browser)
// It re-checks when the user comes back to the app (e.g. from Settings) and
// every 3 minutes while the screen is on, and closes itself once a fix comes in.
// Background checks accept a fix up to 3 minutes old, so they reuse the one the
// tracking already took instead of waking the GPS again.
import { useCallback, useEffect, useRef, useState } from "react";
import { MapPin, MapPinOff, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

const LOCATION_ROLES = ["SE", "ASM", "ZSM", "RSM", "SALES_HEAD"];
const RECHECK_MS = 3 * 60 * 1000;

const ua = () => (typeof navigator !== "undefined" ? navigator.userAgent || "" : "");
const isIOS = () => /iPhone|iPad|iPod/i.test(ua());
const isPhone = () => /Android|iPhone|iPad|iPod/i.test(ua());
const isInstalledApp = () => {
  try {
    return window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;
  } catch { return false; }
};

async function permissionState() {
  try {
    if (navigator.permissions?.query) {
      return (await navigator.permissions.query({ name: "geolocation" })).state; // granted | prompt | denied
    }
  } catch { /* not supported (older iPhone Safari) */ }
  return "unknown";
}

function blockedSteps() {
  if (isIOS()) {
    return [
      "Open the iPhone Settings app.",
      "Tap Privacy & Security → Location Services and turn it on.",
      "In the same list tap Safari Websites (or the Tulsi app) → choose While Using the App.",
      "Come back here.",
    ];
  }
  if (isInstalledApp()) {
    return [
      "Press and hold the Tulsi SFA icon on your home screen.",
      "Tap App info (ⓘ) → Permissions → Location.",
      "Choose Allow.",
      "Come back here.",
    ];
  }
  return [
    "Tap ⋮ (top right) → Settings → Site settings → Location.",
    "Find this site under Blocked and choose Allow.",
    "Come back here and tap Done — check again.",
  ];
}

function gpsOffSteps() {
  return isIOS()
    ? ["Open Settings → Privacy & Security → Location Services and turn it on.", "Come back here."]
    : ["Pull down from the top of the screen.", "Tap Location to turn it on.", "Come back here."];
}

export default function LocationRequiredPopup({ user }) {
  const enabled = LOCATION_ROLES.includes(user?.role_code) && isPhone()
    && typeof navigator !== "undefined" && !!navigator.geolocation;
  const [state, setState] = useState("ok"); // ok | prompt | denied | off
  const [busy, setBusy] = useState(false);
  const offStreak = useRef(0);
  const stateRef = useRef("ok");
  const mounted = useRef(false);
  stateRef.current = state;

  const show = useCallback((next) => {
    if (!mounted.current) return;
    if (next === "ok" && stateRef.current !== "ok") {
      // Several checks can be answered by the same fix — thank the user once.
      toast.success("Location is on. Thank you!", { id: "location-on" });
    }
    stateRef.current = next;
    setState(next);
  }, []);

  // ask=true: the user tapped a button — allowed to bring up the browser /
  // phone boxes and wake the GPS. ask=false: quiet background check.
  const check = useCallback(async (ask = false) => {
    const perm = await permissionState();
    if (!mounted.current) return;
    if (perm === "denied") {
      if (ask) toast.error("Location is still blocked. Follow the steps above.", { id: "location-blocked" });
      show("denied");
      return;
    }
    if (perm === "prompt" && !ask) { show("prompt"); return; }
    if (ask) setBusy(true);
    navigator.geolocation.getCurrentPosition(
      () => {
        offStreak.current = 0;
        if (ask && mounted.current) setBusy(false);
        show("ok");
      },
      (err) => {
        if (!mounted.current) return;
        if (ask) setBusy(false);
        if (err.code === 1) {
          // Permission granted but still refused = the phone's location is off (Android).
          show(perm === "granted" ? "off" : "denied");
        } else if (err.code === 2) {
          // "Position unavailable" can also be a weak signal for a moment — only
          // call it "off" when it happens twice in a row (or the user asked).
          offStreak.current += 1;
          if (ask || offStreak.current >= 2) show("off");
        } else {
          // timeout: weak signal, not "off" — leave the screen as it is
          offStreak.current = 0;
          if (ask) toast.error("Could not get your location yet. Step outside or near a window and try again.");
        }
      },
      ask
        ? { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 }
        : { enableHighAccuracy: false, timeout: 15000, maximumAge: RECHECK_MS },
    );
  }, [show]);

  useEffect(() => {
    if (!enabled) return undefined;
    mounted.current = true;
    let alive = true;
    let permStatus = null;
    check(false);
    const onVis = () => { if (document.visibilityState === "visible") check(false); };
    document.addEventListener("visibilitychange", onVis);
    const t = setInterval(() => { if (document.visibilityState === "visible") check(false); }, RECHECK_MS);
    (async () => {
      try {
        if (navigator.permissions?.query) {
          const st = await navigator.permissions.query({ name: "geolocation" });
          if (!alive) return;
          permStatus = st;
          permStatus.onchange = () => check(false);
        }
      } catch { /* not supported */ }
    })();
    return () => {
      alive = false;
      mounted.current = false;
      document.removeEventListener("visibilitychange", onVis);
      clearInterval(t);
      if (permStatus) permStatus.onchange = null;
    };
  }, [enabled, check]);

  if (!enabled || state === "ok") return null;

  const content = {
    prompt: {
      icon: MapPin,
      title: "Allow location",
      text: "Tulsi SFA needs your location to punch in, start calls and record your route. Tap the button below and choose Allow.",
      action: "Allow location",
      steps: null,
    },
    off: {
      icon: MapPinOff,
      title: "Turn on location",
      text: "Your phone's location (GPS) is off. Calls and your route can't be recorded without it.",
      action: "Turn on location",
      steps: gpsOffSteps(),
    },
    denied: {
      icon: MapPinOff,
      title: "Location is blocked",
      text: "Location is blocked for this app. Turn it on in your phone's settings:",
      action: null,
      steps: blockedSteps(),
    },
  }[state];
  const Icon = content.icon;

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 flex overflow-y-auto p-4"
         role="alertdialog" aria-modal="true" aria-labelledby="loc-popup-title"
         data-testid="location-required-popup">
      <div className="m-auto w-full max-w-sm bg-white rounded-2xl shadow-xl p-5 space-y-4">
        <div className="flex flex-col items-center text-center gap-2">
          <div className="h-14 w-14 rounded-full bg-rose-50 text-rose-600 flex items-center justify-center">
            <Icon className="h-7 w-7" />
          </div>
          <h2 id="loc-popup-title" className="text-lg font-bold text-neutral-900">{content.title}</h2>
          <p className="text-sm text-neutral-600">{content.text}</p>
        </div>

        {content.action && (
          <Button className="w-full h-11" onClick={() => check(true)} disabled={busy}
                  data-testid="location-popup-action">
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <MapPin className="h-4 w-4 mr-2" />}
            {content.action}
          </Button>
        )}

        {content.steps && (
          <div className="rounded-xl bg-neutral-50 border border-neutral-200 p-3">
            {state === "off" && <div className="text-xs font-semibold text-neutral-700 mb-1.5">Or turn it on yourself:</div>}
            <ol className="list-decimal pl-5 space-y-1 text-sm text-neutral-700">
              {content.steps.map((s) => <li key={s}>{s}</li>)}
            </ol>
          </div>
        )}

        {state !== "prompt" && (
          <Button variant="outline" className="w-full h-auto min-h-10 whitespace-normal" onClick={() => check(true)} disabled={busy}
                  data-testid="location-popup-recheck">
            <RefreshCw className="h-4 w-4 mr-2 shrink-0" /> Done — check again
          </Button>
        )}
      </div>
    </div>
  );
}
